import ExcelJS from 'exceljs';
import type { DeliveryTemplate, MappingFieldValue } from '@/utils/templateParser';
import type { KeepListView } from '@/utils/keepByCustomer';

export interface CustomerRow {
  id: number;
  name: string;
  nickname: string;
  phone: string;
  address: string;
  deliveryNotes: string;
  productName?: string; // 상품명 필드 추가
  row_order?: number;
  order_id?: number | null; // 정산서 순서 정렬 키 (배송엑셀 직접 추가 행 = null)
}

interface ParsingOptions {
  parseNickname: boolean;
  parseProductName: boolean;
}

export const downloadExcel = async (rows: CustomerRow[], options: ParsingOptions) => {
  // 새 워크북 생성
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Seller-Helper";
  workbook.lastModifiedBy = "Seller-Helper";
  workbook.created = new Date();
  workbook.modified = new Date();
  
  // 워크시트 추가
  const worksheet = workbook.addWorksheet("배송정보", {
    pageSetup: { paperSize: 9, orientation: 'portrait' } // A4 세로 방향
  });
  
  // 열 정의
  const columns = [
    { header: '순서', key: 'order', width: 5 },
  ];

  if (options.parseNickname) {
    columns.push({ header: '닉네임', key: 'nickname', width: 20 });
  }
  columns.push({ header: '이름', key: 'name', width: 15 });
  columns.push({ header: '전화번호', key: 'phone', width: 20 });
  columns.push({ header: '주소', key: 'address', width: 60 });

  if (options.parseProductName) {
    columns.push({ header: '상품명', key: 'productName', width: 50 });
  }
  columns.push({ header: '배송메모', key: 'deliveryNotes', width: 60 });

  worksheet.columns = columns;
  
  // 헤더 스타일 설정
  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '15B8A6' } // 테이블 헤더와 같은 색상
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      bold: true,
      size: 11
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center'
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });
  
  // 데이터 추가
  rows.forEach((row, index) => {
    // isEmptyFieldValue와 유사한 로직 적용 (특히 deliveryNotes)
    const isEmpty = (value: string | undefined | null) => {
      if (value === null || value === undefined) return true;
      const lower = String(value).toLowerCase().trim();
      return ["string", "", "null", "없음"].includes(lower);
    };

    const deliveryNotesValue = isEmpty(row.deliveryNotes) ? "" : row.deliveryNotes;

    const rowData: any = {
      order: index + 1,
      name: isEmpty(row.name) ? "" : row.name,
      phone: isEmpty(row.phone) ? "" : row.phone,
      address: isEmpty(row.address) ? "" : row.address,
      deliveryNotes: deliveryNotesValue
    };

    if (options.parseNickname) {
      rowData.nickname = isEmpty(row.nickname) ? "" : row.nickname;
    }
    if (options.parseProductName) {
      rowData.productName = isEmpty(row.productName) ? "" : row.productName;
    }

    const addedRow = worksheet.addRow(rowData);

    // 행 높이를 셀 내용에 맞게 추정하여 설정
    const estimateWrappedLines = (text: string, key: string): number => {
      if (!text) return 1;
      const col = worksheet.getColumn(key as any);
      const colWidth = typeof col.width === 'number' && col.width > 0 ? col.width : 10;
      const charsPerLine = Math.max(1, Math.floor(colWidth * 1.1)); // 약간의 여유
      return text.split('\n').reduce((sum, segment) => {
        const len = String(segment).length;
        return sum + Math.max(1, Math.ceil(len / charsPerLine));
      }, 0);
    };

    let maxLines = 1;
    maxLines = Math.max(maxLines, estimateWrappedLines(rowData.address, 'address'));
    maxLines = Math.max(maxLines, estimateWrappedLines(deliveryNotesValue, 'deliveryNotes'));
    if (options.parseProductName) {
      maxLines = Math.max(maxLines, estimateWrappedLines(rowData.productName || '', 'productName'));
    }
    addedRow.height = Math.max(15, 15 * maxLines);
  });
  
  // 추가 빈 행 5개 추가
  for (let i = 0; i < 5; i++) {
    const emptyRowData: any = { order: rows.length + i + 1, name: '', phone: '', address: '', deliveryNotes: ''};
    if (options.parseNickname) {
      emptyRowData.nickname = '';
    }
    if (options.parseProductName) {
      emptyRowData.productName = '';
    }
    const emptyRow = worksheet.addRow(emptyRowData);
    
    // 빈 행도 테두리와 스타일 적용
    emptyRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      
      // 빈 행 배경색 설정 (짝수/홀수 규칙 유지)
      if ((rows.length + i + 1) % 2 === 0) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F9FAFB' } // 짝수 행 배경색 (gray-50)
        };
      }
      
      // 정렬 설정 - options에 따라 컬럼 인덱스가 달라지므로, key 기반으로 스타일 적용
      const key = worksheet.getColumn(cell.col).key;
      if (key === 'order') {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      } else if (key === 'nickname' || key === 'name' || key === 'phone') {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      } else if (key === 'address' || key === 'deliveryNotes' || key === 'productName') {
        cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      }
    });
  }
  
  // 데이터 행 스타일 설정
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 1) { // 헤더를 제외한 데이터 행
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        
        // 정렬 설정 - options에 따라 컬럼 인덱스가 달라지므로, key 기반으로 스타일 적용
        const key = worksheet.getColumn(cell.col).key;
        if (key === 'order') {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else if (key === 'nickname' || key === 'name' || key === 'phone') {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else if (key === 'address' || key === 'deliveryNotes' || key === 'productName') {
          cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        }
      });
      
      // 짝수/홀수 행 배경색 설정
      if (rowNumber % 2 === 0) {
        row.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F9FAFB' } // 짝수 행 배경색 (gray-50)
          };
        });
      }
    }
  });
  
  // 테두리 설정
  worksheet.views = [
    { state: 'frozen', ySplit: 1 } // 첫 번째 행 고정
  ];
  
  // 인쇄 영역 설정
  const totalRowsForShipping = worksheet.rowCount;
  const maxColLetterForShipping = String.fromCharCode(65 + worksheet.columnCount - 1);
  worksheet.pageSetup.printArea = `A1:${maxColLetterForShipping}${totalRowsForShipping}`;

  // 파일명 생성 (현재 날짜 포함)
  const today = new Date();
  const dateString = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
  const fileName = `배송정보_${dateString}.xlsx`;
  
  // 파일 다운로드
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  
  window.URL.revokeObjectURL(url);
}; 
import { CustomerTemplate } from '@/components/CustomerTemplateManager';
import { OrderSummaryRow } from '@/types/excel';
import { Item } from '@/types/purchase';

export const downloadOrderSummaryExcel = async (rows: OrderSummaryRow[]) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Seller-Helper";
  workbook.lastModifiedBy = "Seller-Helper";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet("총 주문 내역");

  worksheet.columns = [
    { header: '상품명', key: 'name', width: 50 },
    { header: '수량', key: 'quantity', width: 10 },
    { header: '금액', key: 'price', width: 15 },
  ];

  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '15B8A6' }
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      bold: true,
      size: 11
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center'
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });

  rows.forEach(row => {
    worksheet.addRow(row);
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 1) {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      });
      // 단가 컬럼에 원화 형식 적용
      row.getCell('price').numFmt = '#,##0"원"';
      row.getCell('price').alignment = { vertical: 'middle', horizontal: 'right' };
    }
  });

  // 인쇄 영역 설정
  const totalRowsForSummary = worksheet.rowCount;
  const maxColLetterForSummary = String.fromCharCode(65 + worksheet.columnCount - 1);
  worksheet.pageSetup.printArea = `A1:${maxColLetterForSummary}${totalRowsForSummary}`;

  const today = new Date();
  const dateString = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
  const fileName = `총주문내역_${dateString}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  window.URL.revokeObjectURL(url);
};

export const downloadSettlementExcel = async (customers: CustomerTemplate[], selectedDate: Date) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Seller-Helper";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet("정산서", {
    pageSetup: { paperSize: 9, orientation: 'portrait' }
  });

  // 1. "입금 완료" 상태인 주문만 필터링합니다.
  const paidCustomers = customers.filter(c => c.status === '입금 완료' && !c.is_keep);

  // 대상 주문에 메모가 1건이라도 있을 때만 메모 컬럼을 추가합니다 (메모 없으면 기존 양식 그대로)
  const hasMemo = paidCustomers.some(c => c.memo && c.memo.trim() !== '');

  worksheet.columns = [
    { header: '닉네임', key: 'nickname', width: 25 },
    { header: '주문내역', key: 'orderDetails', width: 50 },
    { header: '총 주문금액', key: 'totalPrice', width: 20 },
    ...(hasMemo ? [{ header: '메모', key: 'memo', width: 30 }] : []),
  ];

  // 안내 문구 추가
  worksheet.insertRow(1, []);
  const infoCell = worksheet.getCell('A1');
  infoCell.value = `'킵' 상태인 주문은 제외됩니다. 동일한 닉네임의 주문들은 병합됩니다.`;
  infoCell.font = { color: { argb: 'FF0000' }, size: 10 };
  worksheet.mergeCells(`A1:${hasMemo ? 'D' : 'C'}1`);

  const headerRow = worksheet.getRow(2);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '10B981' } // Emerald-500
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      bold: true,
      size: 12
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center'
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'D1D5DB' } },
      left: { style: 'thin', color: { argb: 'D1D5DB' } },
      bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
      right: { style: 'thin', color: { argb: 'D1D5DB' } }
    };
  });
  headerRow.height = 22;

  // 2. 동일한 닉네임의 주문들을 병합하고 상품 및 총액, 메모를 취합합니다.
  const customerItemMap = new Map<string, { items: Item[], totalPrice: number, memos: string[] }>();

  paidCustomers.forEach(customer => {
    if (!customerItemMap.has(customer.nickname)) {
      customerItemMap.set(customer.nickname, { items: [], totalPrice: 0, memos: [] });
    }
    const customerData = customerItemMap.get(customer.nickname)!;

    customer.items.forEach(item => {
      const existingItem = customerData.items.find(i => i.name === item.name);
      if (existingItem) {
        existingItem.quantity += item.quantity;
      } else {
        customerData.items.push({ ...item });
      }
    });
    customerData.totalPrice += customer.totalPrice;

    const memo = customer.memo?.trim();
    if (memo && !customerData.memos.includes(memo)) {
      customerData.memos.push(memo);
    }
  });

  // 3. 병합된 데이터로 엑셀 행을 생성합니다.
  let rowIndex = 0;
  customerItemMap.forEach((data, nickname) => {
    const orderDetails = data.items.map(item => `${item.name} (${item.quantity}개) - ${item.price.toLocaleString()}원`).join('\n');
    const memoText = data.memos.join('\n');
    const row = worksheet.addRow({
      nickname: nickname,
      orderDetails: orderDetails,
      totalPrice: data.totalPrice,
      ...(hasMemo ? { memo: memoText } : {}),
    });

    row.getCell('nickname').alignment = { vertical: 'middle', horizontal: 'center' };
    row.getCell('orderDetails').alignment = { wrapText: true, vertical: 'top' };
    const totalPriceCell = row.getCell('totalPrice');
    totalPriceCell.alignment = { vertical: 'middle', horizontal: 'right' };
    totalPriceCell.numFmt = '#,##0"원"';
    if (hasMemo) {
      row.getCell('memo').alignment = { wrapText: true, vertical: 'top' };
    }

    const memoLineCount = hasMemo && memoText ? memoText.split('\n').length : 0;
    const lineCount = Math.max(data.items.length, memoLineCount) || 1;
    row.height = 15 * lineCount;

    const borderStyle: ExcelJS.Border = { style: 'thin', color: { argb: 'D1D5DB' } };

    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {
        top: borderStyle,
        left: borderStyle,
        bottom: borderStyle,
        right: borderStyle,
      };
    });
      
    if (rowIndex % 2 === 1) {
      const fillStyle: ExcelJS.FillPattern = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F9FAFB' } // gray-50
      };
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = fillStyle;
      });
    }
    rowIndex++;
  });

  const totalRows = worksheet.rowCount;
  const maxColLetter = String.fromCharCode(65 + worksheet.columnCount - 1);
  worksheet.pageSetup.printArea = `A1:${maxColLetter}${totalRows}`;

  // 메모 컬럼 추가로 A4 세로 인쇄 폭을 넘어가므로, 폭 기준 1페이지에 맞춰 축소 인쇄
  if (hasMemo) {
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.fitToHeight = 0;
  }

  const dateString = `${selectedDate.getFullYear()}${String(selectedDate.getMonth() + 1).padStart(2, '0')}${String(selectedDate.getDate()).padStart(2, '0')}`;
  const fileName = `정산서_${dateString}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  window.URL.revokeObjectURL(url);
};

/**
 * 택배사 양식 템플릿에 맞춰 엑셀 다운로드
 */
export const downloadExcelWithTemplate = async (
  rows: CustomerRow[],
  template: DeliveryTemplate,
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Seller-Helper";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet("배송정보", {
    pageSetup: { paperSize: 9, orientation: 'portrait' },
  });

  // 템플릿의 컬럼 순서대로 열 정의
  const columns: { header: string; key: string; width: number }[] =
    template.columnMappings.map((mapping, i) => ({
      header: mapping.columnName,
      key: `col_${i}`,
      width: estimateColumnWidth(mapping.mappedField),
    }));

  worksheet.columns = columns;

  const isEmpty = (value: string | undefined | null) => {
    if (value === null || value === undefined) return true;
    const lower = String(value).toLowerCase().trim();
    return ["string", "", "null", "없음"].includes(lower);
  };

  // 데이터 행 추가 (스타일 없이 원본 양식 그대로)
  rows.forEach((row) => {
    const rowData: Record<string, string | number> = {};

    template.columnMappings.forEach((mapping, colIndex) => {
      const key = `col_${colIndex}`;
      const value = getFieldValue(row, mapping.mappedField);
      rowData[key] = isEmpty(value as string) ? '' : value;
    });

    worksheet.addRow(rowData);
  });

  const today = new Date();
  const dateString = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
  const fileName = `배송정보_${template.name}_${dateString}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  window.URL.revokeObjectURL(url);
};

function getFieldValue(
  row: CustomerRow,
  field: MappingFieldValue,
): string | number {
  switch (field) {
    case 'name':
      return row.name;
    case 'phone_number':
      return row.phone;
    case 'address':
      return row.address;
    case 'address_base':
      return splitKoreanAddress(row.address).base;
    case 'address_detail':
      return splitKoreanAddress(row.address).detail;
    case 'delivery_notes':
      return row.deliveryNotes;
    case 'product_name':
      return row.productName ?? '';
    case 'nickname':
      return row.nickname;
    case 'null':
    default:
      return '';
  }
}

/**
 * 한국 주소를 기본주소(도로명/지번까지)와 상세주소(건물명/동/호 이후)로 분리
 * 도로명 주소: "시/도 구/군 도로명 번호" 까지가 기본, 이후가 상세
 * 지번 주소: "시/도 구/군 동/리 번지" 까지가 기본, 이후가 상세
 */
function splitKoreanAddress(address: string): { base: string; detail: string } {
  if (!address) return { base: '', detail: '' };

  // 도로명 주소: "~로/길/대로 숫자(-숫자)" 패턴 이후를 상세주소로 분리
  const roadMatch = address.match(
    /^(.+(?:로|길|대로)\s*\d+(?:-\d+)?)\s+(.+)$/
  );
  if (roadMatch) {
    return { base: roadMatch[1].trim(), detail: roadMatch[2].trim() };
  }

  // 지번 주소: "~동/리/가 숫자(-숫자)(번지)" 패턴 이후를 상세주소로 분리
  const jibunMatch = address.match(
    /^(.+(?:동|리|가)\s*\d+(?:-\d+)?(?:번지)?)\s+(.+)$/
  );
  if (jibunMatch) {
    return { base: jibunMatch[1].trim(), detail: jibunMatch[2].trim() };
  }

  // 패턴 미매칭 시 전체를 기본주소로
  return { base: address, detail: '' };
}

function estimateColumnWidth(field: MappingFieldValue): number {
  switch (field) {
    case 'address':
    case 'address_base':
      return 50;
    case 'address_detail':
    case 'delivery_notes':
    case 'product_name':
      return 40;
    case 'name':
    case 'phone_number':
    case 'nickname':
      return 20;
    default:
      return 15;
  }
}

export const downloadFilteredOrdersExcel = async (customers: CustomerTemplate[], selectedDate: Date) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Seller-Helper";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet("필터링된 주문 목록", {
    pageSetup: { paperSize: 9, orientation: 'landscape' }
  });

  // ── 상품 집계 ('고객 닉네임' 제외) ──────────────────────────────
  const summaryMap = new Map<string, { name: string; price: number; totalQuantity: number; totalSales: number }>();
  customers
    .filter(c => c.nickname !== '고객 닉네임')
    .forEach(customer => {
      customer.items.forEach(item => {
        const key = `${item.name}__${item.price}`;
        const existing = summaryMap.get(key);
        if (existing) {
          summaryMap.set(key, { name: item.name, price: item.price, totalQuantity: existing.totalQuantity + item.quantity, totalSales: existing.totalSales + item.price * item.quantity });
        } else {
          summaryMap.set(key, { name: item.name, price: item.price, totalQuantity: item.quantity, totalSales: item.price * item.quantity });
        }
      });
    });
  const summaryRows = Array.from(summaryMap.values())
    .sort((a, b) => b.totalQuantity - a.totalQuantity);
  const summaryTotalQty = summaryRows.reduce((s, r) => s + r.totalQuantity, 0);
  const summaryTotalSales = summaryRows.reduce((s, r) => s + r.totalSales, 0);

  // ── 컬럼 설정 (상품 요약: A~D, 주문 목록: A~G) ──────────────────
  worksheet.columns = [
    { key: 'c1', width: 40 },  // A: 상품명 / 고객 닉네임
    { key: 'c2', width: 20 },  // B: 단가   / 주문 시간
    { key: 'c3', width: 15 },  // C: 총수량 / 총 구매액
    { key: 'c4', width: 18 },  // D: 총매출 / 주문 상태
    { key: 'c5', width: 60 },  // E: (empty) / 상품 목록
    { key: 'c6', width: 10 },  // F: (empty) / 킵 여부
    { key: 'c7', width: 40 },  // G: (empty) / 메모
  ];

  const tealFill: ExcelJS.FillPattern = { type: 'pattern', pattern: 'solid', fgColor: { argb: '15B8A6' } };
  const grayFill: ExcelJS.FillPattern = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E5E7EB' } };
  const borderThin: ExcelJS.Border = { style: 'thin', color: { argb: 'D1D5DB' } };

  // ── Row 1: 안내 문구 ───────────────────────────────────────────
  const noticeRow = worksheet.addRow(["'고객 닉네임'인 닉네임의 주문은 상품 요약 및 주문 상세 목록에서 제외됩니다.", '', '', '']);
  worksheet.mergeCells(`A${noticeRow.number}:D${noticeRow.number}`);
  const noticeCell = noticeRow.getCell(1);
  noticeCell.font = { color: { argb: 'DC2626' }, size: 10 };
  noticeCell.alignment = { vertical: 'middle', horizontal: 'left' };
  noticeRow.height = 18;

  // ── Row 2: 상품 요약 섹션 헤더 ─────────────────────────────────
  const summaryHeaderRow = worksheet.addRow(['▼ 상품 요약', '', '', '']);
  worksheet.mergeCells(`A${summaryHeaderRow.number}:D${summaryHeaderRow.number}`);
  const shCell = summaryHeaderRow.getCell(1);
  shCell.fill = tealFill;
  shCell.font = { color: { argb: 'FFFFFF' }, bold: true, size: 12 };
  shCell.alignment = { vertical: 'middle', horizontal: 'left' };
  summaryHeaderRow.height = 22;

  // ── Row 3: 상품 요약 컬럼 헤더 ─────────────────────────────────
  const summaryColHeaderRow = worksheet.addRow(['상품명', '단가', '총수량', '총매출']);
  summaryColHeaderRow.eachCell({ includeEmpty: false }, (cell) => {
    cell.fill = tealFill;
    cell.font = { color: { argb: 'FFFFFF' }, bold: true, size: 11 };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = { top: borderThin, left: borderThin, bottom: borderThin, right: borderThin };
  });
  summaryColHeaderRow.height = 20;

  // ── Row 4~N: 상품 행 ───────────────────────────────────────────
  summaryRows.forEach((sr, idx) => {
    const r = worksheet.addRow([sr.name, sr.price, sr.totalQuantity, sr.totalSales]);
    r.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
    r.getCell(2).alignment = { vertical: 'middle', horizontal: 'right' };
    r.getCell(2).numFmt = '#,##0"원"';
    r.getCell(3).alignment = { vertical: 'middle', horizontal: 'right' };
    r.getCell(3).numFmt = '#,##0"개"';
    r.getCell(4).alignment = { vertical: 'middle', horizontal: 'right' };
    r.getCell(4).numFmt = '#,##0"원"';
    r.eachCell({ includeEmpty: false }, (cell) => {
      cell.border = { top: borderThin, left: borderThin, bottom: borderThin, right: borderThin };
    });
    if (idx % 2 === 1) {
      r.eachCell({ includeEmpty: false }, (cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F9FAFB' } };
      });
    }
    r.height = 18;
  });

  // ── 합계 행 ────────────────────────────────────────────────────
  const totalRow = worksheet.addRow(['합계', '', summaryTotalQty, summaryTotalSales]);
  totalRow.getCell(1).font = { bold: true };
  totalRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
  totalRow.getCell(3).font = { bold: true };
  totalRow.getCell(3).alignment = { vertical: 'middle', horizontal: 'right' };
  totalRow.getCell(3).numFmt = '#,##0"개"';
  totalRow.getCell(4).font = { bold: true };
  totalRow.getCell(4).alignment = { vertical: 'middle', horizontal: 'right' };
  totalRow.getCell(4).numFmt = '#,##0"원"';
  totalRow.eachCell({ includeEmpty: false }, (cell) => {
    cell.fill = grayFill;
    cell.border = { top: borderThin, left: borderThin, bottom: borderThin, right: borderThin };
  });
  totalRow.height = 20;

  // ── 빈 구분 행 ─────────────────────────────────────────────────
  worksheet.addRow([]);

  // ── 주문 상세 섹션 헤더 ─────────────────────────────────────────
  const orderSectionRow = worksheet.addRow(['▼ 주문 상세 목록', '', '', '', '', '', '']);
  worksheet.mergeCells(`A${orderSectionRow.number}:G${orderSectionRow.number}`);
  const osCell = orderSectionRow.getCell(1);
  osCell.fill = tealFill;
  osCell.font = { color: { argb: 'FFFFFF' }, bold: true, size: 12 };
  osCell.alignment = { vertical: 'middle', horizontal: 'left' };
  orderSectionRow.height = 22;

  // ── 주문 목록 컬럼 헤더 ────────────────────────────────────────
  const orderColHeaderRow = worksheet.addRow(['고객 닉네임', '주문 시간', '총 구매액', '주문 상태', '상품 목록', '킵 여부', '메모']);
  orderColHeaderRow.eachCell((cell) => {
    cell.fill = tealFill;
    cell.font = { color: { argb: 'FFFFFF' }, bold: true, size: 12 };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = { top: borderThin, left: borderThin, bottom: borderThin, right: borderThin };
  });
  orderColHeaderRow.height = 22;

  // ── 주문 데이터 행 ─────────────────────────────────────────────
  let currentNickname = '';
  let groupIndex = -1;
  const alternatingColors = ['FFFFFF', 'EFF6FF']; // white, blue-50

  customers.filter(c => c.nickname !== '고객 닉네임').forEach((customer) => {
    if (customer.nickname !== currentNickname) {
      currentNickname = customer.nickname;
      groupIndex++;
    }
    const fillColor = alternatingColors[groupIndex % 2];

    const itemsDetails = customer.items.map(item => `${item.name} (${item.quantity}개) - ${item.price.toLocaleString()}원`).join('\n');

    const row = worksheet.addRow([
      customer.nickname,
      customer.createdAt ? new Date(customer.createdAt) : '',
      customer.totalPrice,
      customer.status,
      itemsDetails,
      customer.is_keep ? '예' : '아니오',
      customer.memo,
    ]);

    row.getCell(2).numFmt = 'yyyy-mm-dd hh:mm:ss';
    row.getCell(3).numFmt = '#,##0"원"';

    row.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    row.getCell(2).alignment = { vertical: 'middle', horizontal: 'center' };
    row.getCell(3).alignment = { vertical: 'middle', horizontal: 'right' };
    row.getCell(4).alignment = { vertical: 'middle', horizontal: 'center' };
    row.getCell(5).alignment = { wrapText: true, vertical: 'top' };
    row.getCell(6).alignment = { vertical: 'middle', horizontal: 'center' };
    row.getCell(7).alignment = { wrapText: true, vertical: 'top' };

    const lineCount = customer.items.length || 1;
    row.height = 15 * lineCount;

    const fillStyle: ExcelJS.Fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: fillColor }
    };

    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = { top: borderThin, left: borderThin, bottom: borderThin, right: borderThin };
      if (fillColor !== 'FFFFFF') {
        cell.fill = fillStyle;
      }
    });
  });

  const totalRows = worksheet.rowCount;
  worksheet.pageSetup.printArea = `A1:G${totalRows}`;

  const dateString = `${selectedDate.getFullYear()}${String(selectedDate.getMonth() + 1).padStart(2, '0')}${String(selectedDate.getDate()).padStart(2, '0')}`;
  const fileName = `주문목록_${dateString}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  window.URL.revokeObjectURL(url);
};
/**
 * 고객별 킵 포장목록 — 시트 2장(상품별/고객별).
 * 포장 흐름이 "상품별로 꺼내서 → 고객별로 담기"라 종이 한 장에 둘 다 필요하다(스펙 §3.3).
 * 정렬은 화면 sortConfig가 아니라 buildKeepListView의 고정 규칙을 그대로 쓴다.
 */
export const downloadKeepListExcel = async (view: KeepListView, loadedDays: number) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Seller-Helper";
  workbook.created = new Date();
  workbook.modified = new Date();

  const rangeNote = `조회 범위: 최근 ${loadedDays}일 주문 · 향후 30일 예약 포함`;
  const borderStyle: ExcelJS.Border = { style: 'thin', color: { argb: 'D1D5DB' } };

  // 한 줄에 17pt — 기본 11pt 글꼴에 필요한 최소치(약 14.5pt)보다 여유를 둬야 줄이 붙어 보이지 않는다.
  // 엑셀 행 높이 한계는 409pt라 24줄(408pt)에서 끊고 다음 행으로 이어 붙인다.
  // 잘라내면 포장 목록에서 항목이 사라진다 — 프로드 실측으로 90일 창에 고객별 43줄·상품별 28줄 행이 실재한다.
  const LINE_HEIGHT = 17;
  const MAX_LINES_PER_ROW = 24;

  const addSheet = (
    name: string,
    columns: { header: string; key: string; width: number }[],
    rows: { label: string; total: number; detailLines: string[] }[],
  ) => {
    const worksheet = workbook.addWorksheet(name, { pageSetup: { paperSize: 9, orientation: 'portrait' } });
    worksheet.columns = columns;

    worksheet.insertRow(1, []);
    const infoCell = worksheet.getCell('A1');
    infoCell.value = rangeNote;
    // 경고가 아니라 단순 범위 안내라 회색 — 빨강은 '킵 제외' 같은 주의 문구에만 쓴다
    infoCell.font = { color: { argb: '6B7280' }, size: 10 };
    infoCell.alignment = { vertical: 'middle', indent: 1 };
    worksheet.getRow(1).height = 20;
    worksheet.mergeCells(`A1:${String.fromCharCode(65 + columns.length - 1)}1`);

    const headerRow = worksheet.getRow(2);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '10B981' } }; // Emerald-500
      cell.font = { color: { argb: 'FFFFFF' }, bold: true, size: 12 };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: borderStyle, left: borderStyle, bottom: borderStyle, right: borderStyle };
    });
    headerRow.height = 24;
    // 헤더 고정 — 아래로 스크롤해도 어느 열인지 보인다.
    // 격자선은 끈다 — 표에 이미 테두리가 있어 격자선이 겹치면 빈 칸까지 표처럼 읽힌다
    worksheet.views = [{ state: 'frozen', ySplit: 2, showGridLines: false }];

    const detailWidth = columns.find((c) => c.key === 'detail')?.width ?? 50;
    // 항목 한 줄이 컬럼 폭에서 몇 줄로 감기는지 — 한글은 엑셀 폭 단위로 2칸.
    // 개행 수만 세면 긴 상품명(실측 최대 39자)에서 과소 계산되어 인쇄 시 잘린다.
    const wrappedLines = (line: string) => {
      const units = [...line].reduce((w, ch) => w + (ch.charCodeAt(0) > 0x2e80 ? 2 : 1), 0);
      return Math.max(1, Math.ceil(units / detailWidth));
    };

    rows.forEach((data, index) => {
      // 26줄 단위로 끊되 항목 중간에서는 자르지 않는다
      const chunks: string[][] = [[]];
      let lines = 0;
      for (const line of data.detailLines) {
        const w = wrappedLines(line);
        if (lines + w > MAX_LINES_PER_ROW && chunks[chunks.length - 1].length > 0) {
          chunks.push([]);
          lines = 0;
        }
        chunks[chunks.length - 1].push(line);
        lines += w;
      }

      chunks.forEach((chunk, chunkIndex) => {
        // 이어지는 행은 닉네임·총 수량을 비워 둔다 — 같은 고객(상품)의 연속임이 보이게
        const row = worksheet.addRow({
          label: chunkIndex === 0 ? data.label : '',
          total: chunkIndex === 0 ? data.total : '',
          detail: chunk.join('\n'),
        });
        // 세로는 세 열 모두 top — 여러 줄 셀에서 첫 줄 기준선이 맞아야 한 행이 한 덩어리로 읽힌다.
        // 총 수량은 가운데 정렬: 오른쪽 정렬이면 바로 옆 상세 텍스트에 숫자가 달라붙는다.
        row.getCell('label').alignment = { vertical: 'top', horizontal: 'left', indent: 1 };
        row.getCell('total').alignment = { vertical: 'top', horizontal: 'center' };
        row.getCell('detail').alignment = { wrapText: true, vertical: 'top', indent: 1 };
        row.getCell('total').numFmt = '0"개"';
        row.height = LINE_HEIGHT * chunk.reduce((sum, line) => sum + wrappedLines(line), 0);
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = { top: borderStyle, left: borderStyle, bottom: borderStyle, right: borderStyle };
          // 줄무늬는 논리 행 기준 — 이어지는 행이 같은 색이라 한 덩어리로 읽힌다
          if (index % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F9FAFB' } };
        });
      });
    });

    // 인쇄해서 포장하는 용도 — 폭 기준 1페이지 축소 + 2페이지부터 헤더 반복
    worksheet.pageSetup.printArea = `A1:${String.fromCharCode(65 + columns.length - 1)}${worksheet.rowCount}`;
    worksheet.pageSetup.printTitlesRow = '1:2';
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.fitToHeight = 0;
  };

  addSheet(
    '상품별',
    [
      { header: '상품명', key: 'label', width: 38 },
      { header: '총 수량', key: 'total', width: 12 },
      { header: '고객별 수량', key: 'detail', width: 50 },
    ],
    view.products.map((p) => ({
      label: p.name,
      total: p.totalQuantity,
      detailLines: p.nicknames.map((n) => `${n.nickname} (${n.quantity}개)`),
    })),
  );

  addSheet(
    '고객별',
    [
      { header: '닉네임', key: 'label', width: 24 },
      { header: '상품 목록', key: 'detail', width: 50 },
      { header: '총 수량', key: 'total', width: 12 },
    ],
    view.customers.map((c) => ({
      label: c.nickname,
      detailLines: c.items.map((i) => `${i.name} (${i.quantity}개)`),
      total: c.totalQuantity,
    })),
  );

  const now = new Date();
  const dateString = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `킵목록_${dateString}.xlsx`;
  link.click();

  window.URL.revokeObjectURL(url);
};
